import React, { useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { SearchIcons } from "../../Icons";
import ListItem from "../../Modals/AddAccount/partials/ListItem";
import CommonButton from "../../Buttons/CommonButton";
import EmptyBeneficiaryIcon from "../../Icons/EmptyBeneficiaryIcon";
import { useModules } from "../../../hooks";
import Dropdown from "../../Dropdown";

const ClientAccountsList = ({
  clientsAccounts,
  CloseAddAcccount,
  setCloseAddAccount,
  handleSelectAccount,
  type = "account",
  cardSubType,
  setCardSubType,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);

  const { short_name, terms_and_conditions_url } = useSelector(
    (state) => state.ownCompany.company
  );
  const isMatchMoveEnabled = useModules("MatchMove");

  // Optimized: Memoize filtered accounts to prevent recalculation on every render
  const filteredAccounts = useMemo(() => {
    if (!searchQuery.trim()) return clientsAccounts;

    const query = searchQuery.toLowerCase();
    return clientsAccounts.filter((account) =>
      account.name.toLowerCase().includes(query)
    );
  }, [clientsAccounts, searchQuery]);

  // Optimized: Memoize search handler
  const handleSearchChange = useCallback(
    (e) => setSearchQuery(e.target.value),
    []
  );

  // Optimized: Memoize account selection handler
  const handleAccountClick = useCallback(
    (account) => setCloseAddAccount(account),
    [setCloseAddAccount]
  );

  // Optimized: Memoize checkbox handler
  const handleTermsChange = useCallback(
    (e) => setAcceptTerms(e.target.checked),
    []
  );

  // Optimized: Memoize button disabled state
  const isButtonDisabled = useMemo(
    () =>
      !CloseAddAcccount?.id ||
      (type === "card" && (!acceptTerms || !cardSubType)),
    [CloseAddAcccount?.id, type, acceptTerms, cardSubType]
  );

  // Optimized: Memoize terms URL
  const termsUrl = useMemo(
    () =>
      isMatchMoveEnabled
        ? "https://matchmove.com/terms"
        : terms_and_conditions_url,
    [isMatchMoveEnabled, terms_and_conditions_url]
  );

  const hasResults = filteredAccounts.length > 0;
  const showTerms = type === "card";

  return (
    <div>
      <h2 className="pb-3 text-sm font-medium text-text-primary">
        Choose your account
      </h2>

      <div className="search-input-wrapper w-full px-3 py-1 rounded-full flex justify-start items-center">
        <SearchIcons className="w-6 h-6 relative" />
        <input
          onChange={handleSearchChange}
          value={searchQuery}
          placeholder="Search"
          className="search-input"
          aria-label="Search accounts"
        />
      </div>

      {hasResults ? (
        <>
          {filteredAccounts.map((account) => (
            <div key={account.id} onClick={() => handleAccountClick(account)}>
              <ListItem
                title={account.name}
                subTitle={`${short_name} account`}
                selectedItem={CloseAddAcccount}
                itemId={account.id}
              />
            </div>
          ))}

          {type === "card" && CloseAddAcccount?.id && (
            <div className="mt-4 relative overflow-visible">
              <Dropdown
                label="Card Type"
                name="sub_type"
                placeholder="Select card type"
                options={[
                  { label: "Physical card", value: "physical" },
                  { label: "Virtual card", value: "virtual" },
                ]}
                value={cardSubType}
                onChange={(e) => setCardSubType(e.target.value)}
              />
            </div>
          )}

          {showTerms && (
            <div className="flex space-x-2 pt-6 pb-2">
              <input
                type="checkbox"
                id="accept_terms"
                name="accept_terms"
                checked={acceptTerms}
                onChange={handleTermsChange}
                required
                className="checkbox-input"
              />
              <label
                htmlFor="accept_terms"
                className="secondry-lable text-sm cursor-pointer"
              >
                By signing up, you agree to
                <a
                  href={termsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="primary-lable px-2 cursor-pointer"
                >
                  {isMatchMoveEnabled ? "MatchMove" : "our"} Terms & Conditions.
                </a>
              </label>
            </div>
          )}

          <div className="mt-8 sticky bottom-9">
            <CommonButton
              onClick={handleSelectAccount}
              disabled={isButtonDisabled}
            >
              Continue
            </CommonButton>
          </div>
        </>
      ) : (
        <div className="flex flex-col text-text-primary items-center justify-center py-6">
          <EmptyBeneficiaryIcon className="w-40 h-40" />
          <p>No account found</p>
        </div>
      )}
    </div>
  );
};

export default React.memo(ClientAccountsList);
